import { Button, MenuItem } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { Select } from '../../Select/Select';
import { SeveranceFormValues } from '../SeverancePage.hooks';

const INITIAL_VALUES: SeveranceFormValues = {
  monthlySalary: 400,
  annualBonus: 0,
  workingYears: 10,
  workingMonths: 0,
};

const schema = yup.object().shape({
  monthlySalary: yup.number().min(1, '1만 원 이상 입력해 주세요.').max(10_000, '최대 1억 원까지 입력할 수 있습니다.').required('필수 입력 항목입니다.'),
  annualBonus: yup.number().min(0, '0 이상이어야 합니다.').max(50_000, '최대 50,000만 원까지 입력할 수 있습니다.').required('필수 입력 항목입니다.'),
  workingYears: yup.number().min(1, '1년 이상 입력해 주세요.').max(40, '최대 40년까지 계산합니다.').required('필수 입력 항목입니다.'),
  workingMonths: yup.number().min(0).max(11).required('필수 입력 항목입니다.'),
});

export interface SeveranceFormProps {
  onSubmit: (values: SeveranceFormValues) => void;
  className?: string;
}

export function SeveranceForm({ onSubmit, className }: SeveranceFormProps) {
  const handleSubmit = useCallback((values: SeveranceFormValues) => onSubmit(values), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue, values }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="monthlySalary">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="월 평균 급여 (만 원)"
                  placeholder="400"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlySalary && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.monthlySalary}</div>}

            <Field name="annualBonus">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="연간 상여금 (만 원)"
                  placeholder="0"
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.annualBonus && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.annualBonus}</div>}

            <Field name="workingYears">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="근속 연수 (년)"
                  placeholder="10"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.workingYears && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.workingYears}</div>}

            <Field name="workingMonths">
              {({ field }: FieldProps) => (
                <Select
                  native={false}
                  label="추가 근속 월수"
                  name={field.name}
                  value={values.workingMonths}
                  onChange={(event) => setFieldValue(field.name, Number(event.target.value))}
                >
                  {Array.from({ length: 12 }, (_, index) => (
                    <MenuItem key={index} value={index}>
                      {index}개월
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large" sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              퇴직금 계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
